import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ARTIFACTS_DATA_PROVIDER, ArtifactsDataProvider } from '@trovata/app/features/ai-artifacts/services/artifacts-data-provider';
import { ArtifactsResponse, DataArtifact } from '@trovata/app/features/ai-artifacts/models/artifacts.model';
import {
	ClearArtifactsContext,
	ClearArtifactsState,
	GetArtifacts,
	InitArtifactsState,
	SelectArtifact,
	SetArtifactsContext,
} from '@trovata/app/features/ai-artifacts/store/actions/artifacts.actions';

// ==========================================
// State Model
// ==========================================

/**
 * ArtifactsStateModel — state shape for the AI Data Artifacts NGXS store.
 *
 * Uses the entity-normalization pattern (mirrors `AgentsState`): `artifactEntities` for O(1)
 * lookups + `artifactIds` for ordered list display. Minimal for P2 (list + open/select); later
 * phases extend it (open artifact data, editor draft, undo/redo).
 */
export class ArtifactsStateModel {
	/** Normalized entity map for O(1) artifact lookups. */
	artifactEntities: Record<string, DataArtifact>;

	/** Ordered array of artifact ids for list display (newest-first from the API). */
	artifactIds: string[];

	/** Currently selected (open) artifact id, or null if none. */
	selectedArtifactId: string | null;

	/** Whether the artifacts list is loading. */
	isListLoading: boolean;

	/** Whether the last list fetch failed. */
	artifactsListError: boolean;

	/**
	 * The EXPLICIT product context (`'cash'` | `'tms'`) declared by the active surface. The reliable SSOT
	 * the data-provider router reads to pick the TMS vs empty-Cash provider. `null` until a surface declares it.
	 */
	explicitContext: 'cash' | 'tms' | null;
}

const DEFAULT_ARTIFACTS_STATE: ArtifactsStateModel = {
	artifactEntities: {},
	artifactIds: [],
	selectedArtifactId: null,
	isListLoading: false,
	artifactsListError: false,
	explicitContext: null,
};

@State<ArtifactsStateModel>({
	name: 'artifacts',
	defaults: DEFAULT_ARTIFACTS_STATE,
})
@Injectable()
export class ArtifactsState {
	private readonly artifactsDataProvider: ArtifactsDataProvider = inject(ARTIFACTS_DATA_PROVIDER);

	// ==========================================
	// Selectors
	// ==========================================

	/** Returns all artifacts denormalized from the entity map, preserving `artifactIds` order. */
	@Selector()
	static artifacts(state: ArtifactsStateModel): DataArtifact[] {
		return state.artifactIds.map((id: string) => state.artifactEntities[id]).filter(Boolean);
	}

	/** Returns the currently selected artifact, or null if none selected. */
	@Selector()
	static selectedArtifact(state: ArtifactsStateModel): DataArtifact | null {
		if (!state.selectedArtifactId) {
			return null;
		}
		return state.artifactEntities[state.selectedArtifactId] ?? null;
	}

	/** Whether the artifacts list is loading. */
	@Selector()
	static isListLoading(state: ArtifactsStateModel): boolean {
		return state.isListLoading;
	}

	/** Whether the last list fetch failed. */
	@Selector()
	static artifactsListError(state: ArtifactsStateModel): boolean {
		return state.artifactsListError;
	}

	/** The explicit product context the data-provider router reads. */
	@Selector()
	static explicitContext(state: ArtifactsStateModel): 'cash' | 'tms' | null {
		return state.explicitContext;
	}

	// ==========================================
	// Action Handlers
	// ==========================================

	/** Initializes the artifacts state (defers the actual fetch to `GetArtifacts`). */
	@Action(InitArtifactsState)
	initArtifactsState(context: StateContext<ArtifactsStateModel>): Observable<void> {
		return context.dispatch(new GetArtifacts());
	}

	/** Clears all artifacts state data. */
	@Action(ClearArtifactsState)
	clearArtifactsState(context: StateContext<ArtifactsStateModel>): void {
		context.setState(DEFAULT_ARTIFACTS_STATE);
	}

	/** Declares the explicit product context for the active artifacts surface. */
	@Action(SetArtifactsContext)
	setArtifactsContext(context: StateContext<ArtifactsStateModel>, action: SetArtifactsContext): void {
		context.patchState({ explicitContext: action.context });
	}

	/** Releases the explicit context on teardown — guarded so it can't wipe a newer surface's context. */
	@Action(ClearArtifactsContext)
	clearArtifactsContext(context: StateContext<ArtifactsStateModel>, action: ClearArtifactsContext): void {
		if (context.getState().explicitContext === action.context) {
			context.patchState({ explicitContext: null });
		}
	}

	/** Fetches the list of artifacts from the API and rebuilds the entity map + id order. */
	@Action(GetArtifacts)
	getArtifacts(context: StateContext<ArtifactsStateModel>): Observable<void> {
		context.patchState({ isListLoading: true, artifactsListError: false });
		return this.artifactsDataProvider.listArtifacts().pipe(
			tap((response: ArtifactsResponse) => {
				const { entities, ids }: { entities: Record<string, DataArtifact>; ids: string[] } = this.normalizeArtifacts(response.artifacts);
				context.patchState({
					artifactEntities: entities,
					artifactIds: ids,
					isListLoading: false,
					artifactsListError: false,
				});
			}),
			map(() => undefined),
			catchError(() => {
				context.patchState({ isListLoading: false, artifactsListError: true });
				return of(undefined);
			})
		);
	}

	/** Selects (opens) an artifact by id, or deselects if null. */
	@Action(SelectArtifact)
	selectArtifact(context: StateContext<ArtifactsStateModel>, action: SelectArtifact): void {
		context.patchState({ selectedArtifactId: action.artifactId });
	}

	// ==========================================
	// Helpers
	// ==========================================

	/** Normalizes an artifact list into an entity map + ordered id array (preserving API order). */
	private normalizeArtifacts(artifacts: readonly DataArtifact[]): { entities: Record<string, DataArtifact>; ids: string[] } {
		const entities: Record<string, DataArtifact> = {};
		const ids: string[] = [];
		for (const artifact of artifacts) {
			entities[artifact.artifactId] = artifact;
			ids.push(artifact.artifactId);
		}
		return { entities, ids };
	}
}
